import { h } from 'vue';

import {
  renderBoolean,
  renderMoney,
  titleCase,
  translateEnum,
  translateNumberedEntity,
} from '@rygen/rygen-ui/i18n';
import { ColumnSpecification } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';
import { buildSearchRoute, formatDateRange } from '@/util';

import CarrierAvatarAndName from '@/components/carrier/CarrierAvatarAndName.vue';
import ClientAvatarAndName from '@/components/client/ClientAvatarAndName.vue';
import GLCodeInlineEdit from '@/components/glCode/GLCodeInlineEdit.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import ShipmentChip from '@/components/shipment/ShipmentChip.vue';
import AssignedUsersSearchResult from '@/components/user/AssignedUsersSearchResult.vue';
import ShipmentFinancialStatusChip from '@rygen/rygen-ui/components/shipment/financials/ShipmentFinancialStatusChip.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';

import { EmbeddableLocation } from '../EmbeddableLocation';
import { ShipmentFinancialStatus } from '../shipment/ShipmentFinancialStatus';
import { ShipmentHealth } from '../shipment/ShipmentHealth';
import { ShipmentStatus } from '../shipment/ShipmentStatus';
import { ShipmentStop } from '../shipment/ShipmentStop';
import { ShipmentStopType } from '../shipment/ShipmentStopType';

import {
  DESTINATION_LOCATION_GROUP_COLUMN,
  ORIGIN_LOCATION_GROUP_COLUMN,
} from './locationGroupColumn';
import { ShipmentColumnProps } from './ShipmentColumnProps';

const tagSearchRouteBuilder = (tag: string) => {
  return buildSearchRoute('shipmentList', { tags: [tag] });
};

const OWNING_CLIENT_COLUMN = new ColumnSpecification({
  field: 'client',
  header: i18n.global.t('clients.clients'),
  body: ({ data }: ShipmentColumnProps) => {
    return h(ClientAvatarAndName, {
      name: data.client?.name,
      logo: data.client?.branding?.avatar?.signedUrl,
      accentColor: data.client?.branding?.accentColor,
    });
  },
});

const STATUS_HEALTH_COLUMN = new ColumnSpecification({
  field: 'status',
  header: `${i18n.global.t('common.status')} / ${i18n.global.t(
    'shipments.health',
  )}`,
  body: ({ data }: ShipmentColumnProps) => {
    return h(ShipmentChip, {
      status: data.status as ShipmentStatus,
      health: data.health as ShipmentHealth,
    });
  },
});

const FINANCIAL_STATUS_COLUMN = new ColumnSpecification({
  field: 'financialStatus',
  header: i18n.global.t('common.paramStatuses', {
    param: i18n.global.t('shipments.financials.financials'),
  }),
  body: ({ data }: ShipmentColumnProps) => {
    return data.financialStatus
      ? h(ShipmentFinancialStatusChip, {
          status: data.financialStatus as ShipmentFinancialStatus,
        })
      : undefined;
  },
});

const TAG_COLUMN = new ColumnSpecification({
  field: 'tags',
  header: i18n.global.t('tags.tags', 2),
  class: 'tags-column',
  body: ({ data }: ShipmentColumnProps) => {
    return h(TagContainer, {
      class: 'h-auto',
      modelValue: data.tags ?? [],
      routeBuilder: tagSearchRouteBuilder,
      readonly: true,
      maxVisible: 2,
    });
  },
});

export const CARRIER_COLUMN = new ColumnSpecification({
  field: 'carrierName',
  header: i18n.global.t('carriers.carriers'),
  body: ({ data }: ShipmentColumnProps) => {
    return h(CarrierAvatarAndName, {
      name: data.carrierName,
      scac: data.scac,
    });
  },
});

export const COST_ESTIMATE_COLUMN = new ColumnSpecification({
  field: 'costEstimate',
  header: i18n.global.t('shipmentFinancials.paramEstimate', {
    param: i18n.global.t('shipmentFinancials.cost'),
  }),
  body: ({ data }: ShipmentColumnProps) => {
    return renderMoney(data.costEstimate);
  },
});

export const ORIGIN_COLUMN = new ColumnSpecification({
  field: 'origin',
  header: i18n.global.t('shipments.origin'),
  body: ({ data }: ShipmentColumnProps) => {
    const shippingPoint = new ShipmentStop({
      stopType: ShipmentStopType.PICKUP,
      location: new EmbeddableLocation({
        locationName: data.originLocationName,
        city: data.originLocality,
        state: data.originRegion,
        postalCode: data.originPostalCode,
      }),
    });

    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

export const DESTINATION_COLUMN = new ColumnSpecification({
  field: 'destination',
  header: i18n.global.t('shipments.destinations'),
  body: ({ data }: ShipmentColumnProps) => {
    const shippingPoint = new ShipmentStop({
      stopType: ShipmentStopType.DROP_OFF,
      location: new EmbeddableLocation({
        locationName: data.destinationLocationName,
        city: data.destinationLocality,
        state: data.destinationRegion,
        postalCode: data.destinationPostalCode,
      }),
    });

    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

const STOP_COUNT_COLUMN = new ColumnSpecification({
  field: 'stopCount',
  header: i18n.global.t('shipments.stopCount'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.stopCount;
  },
});

export const SERVICE_COLUMN = new ColumnSpecification({
  field: 'service',
  header: i18n.global.t('shipments.services'),
  body: ({ data }: ShipmentColumnProps) => {
    return titleCase(data.service);
  },
});

const MODE_COLUMN = new ColumnSpecification({
  field: 'mode',
  header: i18n.global.t('shipments.modes'),
  body: ({ data }: ShipmentColumnProps) =>
    data.mode ? translateEnum(data.mode, 'Mode') : '-',
});

export const REQUESTED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedPickupDateRange',
  header: i18n.global.t('shipments.dates.requestedPickup'),
  body: ({ data }: ShipmentColumnProps) => {
    return formatDateRange(data.requestedPickupDateRange);
  },
});

const SCHEDULED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'scheduledPickupDateRange',
  header: i18n.global.t('shipments.dates.scheduledPickup'),
  body: ({ data }: ShipmentColumnProps) => {
    return formatDateRange(data.scheduledPickupDateRange);
  },
});

const EXPECTED_PICKUP_ARRIVAL_COLUMN = new ColumnSpecification({
  field: 'expectedPickupArrival',
  header: i18n.global.t('shipments.dates.expectedPickupArrival'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.expectedPickupArrivalDate === undefined
      ? '-'
      : i18n.global.d(data.expectedPickupArrivalDate, 'numeric');
  },
});

const ACTUAL_PICKUP_ARRIVAL_COLUMN = new ColumnSpecification({
  alias: ['actualPickup'],
  field: 'actualPickupArrival',
  header: i18n.global.t('shipments.dates.actualPickupArrival'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.actualPickupArrivalDate === undefined
      ? '-'
      : i18n.global.d(data.actualPickupArrivalDate, 'numeric');
  },
});

const ACTUAL_PICKUP_DEPARTURE_COLUMN = new ColumnSpecification({
  alias: ['actualPickup'],
  field: 'actualPickupDeparture',
  header: i18n.global.t('shipments.dates.actualPickupDeparture'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.actualPickupDepartureDate === undefined
      ? '-'
      : i18n.global.d(data.actualPickupDepartureDate, 'numeric');
  },
});

const PICKUP_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'pickupAppointmentRequired',
  header: i18n.global.t('shippingPoints.pickupAppointmentRequired'),
  body: ({ data }: ShipmentColumnProps) =>
    renderBoolean(data.pickupAppointmentRequired),
});

const EXPECTED_DELIVERY_ARRIVAL_COLUMN = new ColumnSpecification({
  field: 'expectedDeliveryArrival',
  header: i18n.global.t('shipments.dates.expectedDeliveryArrival'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.expectedDeliveryArrivalDate === undefined
      ? '-'
      : i18n.global.d(data.expectedDeliveryArrivalDate, 'numeric');
  },
});

const ACTUAL_DELIVERY_ARRIVAL_COLUMN = new ColumnSpecification({
  alias: ['actualDelivery'],
  field: 'actualDeliveryArrival',
  header: i18n.global.t('shipments.dates.actualDeliveryArrival'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.actualDeliveryArrivalDate === undefined
      ? '-'
      : i18n.global.d(data.actualDeliveryArrivalDate, 'numeric');
  },
});

const ACTUAL_DELIVERY_DEPARTURE_COLUMN = new ColumnSpecification({
  alias: ['actualDelivery'],
  field: 'actualDeliveryDeparture',
  header: i18n.global.t('shipments.dates.actualDeliveryDeparture'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.actualDeliveryDepartureDate === undefined
      ? '-'
      : i18n.global.d(data.actualDeliveryDepartureDate, 'numeric');
  },
});

const REQUESTED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedDeliveryDateRange',
  header: i18n.global.t('shipments.dates.requestedDelivery'),
  body: ({ data }: ShipmentColumnProps) => {
    return formatDateRange(data.requestedDeliveryDateRange);
  },
});

const SCHEDULED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'scheduledDeliveryDateRange',
  header: i18n.global.t('shipments.dates.scheduledDelivery'),
  body: ({ data }: ShipmentColumnProps) => {
    return formatDateRange(data.scheduledDeliveryDateRange);
  },
});

const DELIVERY_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'deliveryAppointmentRequired',
  header: i18n.global.t('shippingPoints.deliveryAppointmentRequired'),
  body: ({ data }: ShipmentColumnProps) =>
    renderBoolean(data.deliveryAppointmentRequired),
});

export const WEIGHT_COLUMN = new ColumnSpecification({
  field: 'weight',
  header: i18n.global.t('lineItems.weight'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.weight === undefined
      ? '-'
      : translateNumberedEntity(
          data.weight,
          `uoms.weight.abbreviations.${data.weightUom}`,
        );
  },
});

const HANDLING_UNIT_COUNT = new ColumnSpecification({
  field: 'handlingUnitCount',
  header: i18n.global.t('lineItems.handlingUnits'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.handlingUnitCount === undefined
      ? '-'
      : `${data.handlingUnitCount} ${data.handlingUnitDescription}`;
  },
});

const PRODUCT_NUMBER_COLUMN = new ColumnSpecification({
  field: 'productNumber',
  header: i18n.global.t('lineItems.productNumber'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.productNumber === undefined ? '-' : data.productNumber;
  },
});

export const PRODUCT_DESCRIPTION_COLUMN = new ColumnSpecification({
  alias: ['productionDescription'],
  field: 'productDescription',
  header: i18n.global.t('lineItems.productDescription'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.productDescription === undefined
      ? '-'
      : data.productDescription;
  },
});

const HOT_COLUMN = new ColumnSpecification({
  field: 'hot',
  header: i18n.global.t('common.hot'),
  body: ({ data }: ShipmentColumnProps) => renderBoolean(data.hot),
});

const HAZMAT_COLUMN = new ColumnSpecification({
  field: 'hazmat',
  header: i18n.global.t('lineItems.hazmat'),
  body: ({ data }: ShipmentColumnProps) => renderBoolean(data.hazmat),
});

const PAYABLE_COLUMN = new ColumnSpecification({
  field: 'payable',
  header: i18n.global.t('shipments.payable'),
  body: ({ data }: ShipmentColumnProps) => renderBoolean(data.payable),
});

const GL_CODE_COLUMN = new ColumnSpecification({
  field: 'glCodes',
  header: i18n.global.t('glCodes.glCodes', 2),
  class: 'gl-codes-column',
  body: ({ data }: ShipmentColumnProps) => {
    const glCodes =
      data.relatedGlCodes?.map((g) =>
        h(GLCodeInlineEdit, {
          modelValue: g,
          editable: false,
        }),
      ) ?? [];
    return h('div', { class: 'flex flex-gap-1' }, glCodes);
  },
});

const DIRECTION_COLUMN = new ColumnSpecification({
  field: 'direction',
  header: i18n.global.t('shipments.directions'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.direction ? translateEnum(data.direction, 'Direction') : '-';
  },
});

const BILLING_TERMS_COLUMN = new ColumnSpecification({
  field: 'billingTerms',
  header: i18n.global.t('shipments.billingTerms'),
  body: ({ data }: ShipmentColumnProps) => {
    return data.billingTerms
      ? translateEnum(data.billingTerms, 'BillingTerms')
      : '-';
  },
});

const EQUIPMENT_TYPE_COLUMN = new ColumnSpecification({
  field: 'equipmentType',
  header: i18n.global.t('carriers.equipmentTypes'),
  body: ({ data }: ShipmentColumnProps) => data.equipmentType?.name ?? '-',
});

const FREIGHT_ALLOWANCE_ENABLED_COLUMN = new ColumnSpecification({
  field: '  freightAllowanceEnabled',
  header: i18n.global.t('orders.freightAllowance.freightAllowanceEnabled'),
  body: ({ data }: ShipmentColumnProps) =>
    renderBoolean(data.freightAllowanceEnabled),
});

const FREIGHT_ALLOWANCE_AMOUNT_COLUMN = new ColumnSpecification({
  field: 'freightAllowance',
  header: i18n.global.t('orders.freightAllowance.freightAllowance'),
  body: ({ data }: ShipmentColumnProps) => {
    return renderMoney(data.freightAllowance);
  },
});

const SPOT_MARKET_SUMMARY_COLUMN = new ColumnSpecification({
  field: 'spotMarketRequestCount',
  header: i18n.global.t('carrierPortal.spotMarket.spotMarkets', 2),
  body: ({ data }: ShipmentColumnProps) => {
    const spotMarketCountText = i18n.global.t(
      'carrierPortal.spotMarket.countSpotMarketRequestsSent',
      { count: data.spotMarketRequestCount },
    );
    if (!data.spotMarketRequestCount) {
      return spotMarketCountText;
    }

    const requestEl = h('div', {}, spotMarketCountText);
    const acceptedEl = h(
      'div',
      { class: 'text-gray-darker' },
      i18n.global.t(
        'carrierPortal.spotMarket.countSpotMarketRequestsAccepted',
        {
          count: data.spotMarketAcceptedCount,
        },
      ),
    );
    return h('div', {}, [requestEl, acceptedEl]);
  },
});

const USER_ASSIGNMENT_COLUMN = new ColumnSpecification({
  field: 'assignedUsers',
  header: i18n.global.t('common.assignedParam', {
    param: i18n.global.t('users.users', 2),
  }),
  body: ({ data }: ShipmentColumnProps) =>
    h(AssignedUsersSearchResult, {
      id: 'assign-user-button',
      modelValue: data.assignedUsers || [],
      parentId: data.id,
      ownerId: data.owner?.id,
      type: 'shipment',
    }),
});

const CANCELLATION_REASON_COLUMN = new ColumnSpecification({
  field: 'cancellationReason',
  header: i18n.global.t('orders.cancellationReason'),
  body: ({ data }: ShipmentColumnProps) => data.cancellationReason ?? '-',
});

const REQUESTED_MODE_COLUMN = new ColumnSpecification({
  field: 'requestedMode',
  header: i18n.global.t('common.requestedMode'),
  body: ({ data }: ShipmentColumnProps) =>
    data.requestedMode ? translateEnum(data.requestedMode, 'Mode') : '-',
});

const SOURCE_COLUMN = new ColumnSpecification({
  field: 'source',
  header: i18n.global.t('common.source'),
  body: ({ data }: ShipmentColumnProps) => data.source ?? '-',
});

const SWC_COLUMN = new ColumnSpecification({
  field: 'swc',
  header: i18n.global.t('swc.shipWithoutContact'),
  body: ({ data }: ShipmentColumnProps) => renderBoolean(data.swc ?? false),
});

const CUSTOMER_ROUTED_COLUMN = new ColumnSpecification({
  field: 'customerRouted',
  header: i18n.global.t('common.customerRouted'),
  body: ({ data }: ShipmentColumnProps) =>
    renderBoolean(data.customerRouted ?? false),
});

const MILEAGE_COLUMN = new ColumnSpecification({
  field: 'totalEstimatedMileage',
  header: i18n.global.t('common.mileage'),
  body: ({ data }: ShipmentColumnProps): string =>
    data.totalMileage
      ? i18n.global.t('rateShop.mileage', {
          count: Math.round(data.totalMileage),
        })
      : '-',
});

/**
 * All possible shipment columns
 *
 * Order is significant. This array is used as the default set of columns.
 **/
export const SHIPMENT_COLUMNS: ColumnSpecification[] = [
  OWNING_CLIENT_COLUMN,
  STATUS_HEALTH_COLUMN,
  USER_ASSIGNMENT_COLUMN,
  FINANCIAL_STATUS_COLUMN,
  TAG_COLUMN,
  CARRIER_COLUMN,
  COST_ESTIMATE_COLUMN,
  ORIGIN_COLUMN,
  DESTINATION_COLUMN,
  STOP_COUNT_COLUMN,
  SERVICE_COLUMN,
  MODE_COLUMN,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SCHEDULED_PICKUP_DATE_RANGE_COLUMN,
  EXPECTED_PICKUP_ARRIVAL_COLUMN,
  ACTUAL_PICKUP_ARRIVAL_COLUMN,
  ACTUAL_PICKUP_DEPARTURE_COLUMN,
  REQUESTED_DELIVERY_DATE_RANGE_COLUMN,
  SCHEDULED_DELIVERY_DATE_RANGE_COLUMN,
  EXPECTED_DELIVERY_ARRIVAL_COLUMN,
  ACTUAL_DELIVERY_ARRIVAL_COLUMN,
  ACTUAL_DELIVERY_DEPARTURE_COLUMN,
  GL_CODE_COLUMN,
  WEIGHT_COLUMN,
  HANDLING_UNIT_COUNT,
  PRODUCT_NUMBER_COLUMN,
  PRODUCT_DESCRIPTION_COLUMN,
  HOT_COLUMN,
  PICKUP_APPOINTMENT_REQUIRED_COLUMN,
  DELIVERY_APPOINTMENT_REQUIRED_COLUMN,
  HAZMAT_COLUMN,
  PAYABLE_COLUMN,
  DIRECTION_COLUMN,
  BILLING_TERMS_COLUMN,
  EQUIPMENT_TYPE_COLUMN,
  FREIGHT_ALLOWANCE_AMOUNT_COLUMN,
  FREIGHT_ALLOWANCE_ENABLED_COLUMN,
  SPOT_MARKET_SUMMARY_COLUMN,
  CANCELLATION_REASON_COLUMN,
  REQUESTED_MODE_COLUMN,
  SOURCE_COLUMN,
  SWC_COLUMN,
  CUSTOMER_ROUTED_COLUMN,
  MILEAGE_COLUMN,
  ORIGIN_LOCATION_GROUP_COLUMN,
  DESTINATION_LOCATION_GROUP_COLUMN,
];

export const SHIPMENT_COLUMNS_SORT_FIELDS = SHIPMENT_COLUMNS.map(
  (col) => col.field,
)
  .filter(notNull)
  .concat('id', '');
